import { API_BASE as API } from "./api";

// Shared by every reader and prefetcher of a product's detail —
// ProductDetailPage, ProductCard's hover prefetch, DiscoveryPanel's ratings —
// which MUST all use the identical key and fetch, or a prefetch warms an
// entry the detail page never reads. Keyed per account: a signed-in
// shopper's request carries their token (an age-verified adult may open an
// age-restricted product; anyone else gets a 403), and logout doesn't clear
// react-query's cache, so an authenticated response must never be readable
// by another account, or an anonymous visitor, in the same tab.
export const productQueryKey = (slugOrId, userId = null) => ["product", String(slugOrId), userId ?? "anon"];

// `viewer` comes from useProductViewer(): { userId, headers } when signed in,
// neither when anonymous.
export const productQueryOptions = (slugOrId, { userId = null, headers } = {}) => ({
  queryKey: productQueryKey(slugOrId, userId),
  queryFn: () => fetchProductBySlugOrId(slugOrId, { headers }),
});

/* A product's public slug ends in its id ("…-triple-camera-96"), and a bare
   id is also accepted. Lets the page start fetching reviews at the same
   time as the product instead of after it (speed brief item 22). Null when
   the slug carries no id. */
export function productIdFromSlug(slugOrId) {
  const match = /(?:^|-)(\d+)$/.exec(String(slugOrId || ""));
  return match ? Number(match[1]) : null;
}

export async function fetchProductBySlugOrId(slugOrId, { headers } = {}) {
  const isNumeric = /^\d+$/.test(String(slugOrId));
  const url = isNumeric ? `${API}/products/${slugOrId}` : `${API}/products/slug/${slugOrId}`;
  const res = headers ? await fetch(url, { headers }) : await fetch(url);
  if (res.status === 404) {
    const err = new Error("Product not found");
    err.notFound = true;
    throw err;
  }
  if (res.status === 403) {
    const body = await res.json().catch(() => null);
    const err = new Error("Not available for your account");
    err.ageRestricted = body?.detail === "age_restricted";
    throw err;
  }
  if (!res.ok) throw new Error(res.statusText);
  return res.json();
}
